import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Circle,
  FileText,
  Filter,
  ListTree,
  Pencil,
  RefreshCw,
  Search,
  Shield,
  Trash2,
  X,
  SlidersHorizontal,
  ArrowRight,
} from "lucide-react";

import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import { PageLayout } from "../components/PageLayout";
import { useAudit } from "../context/AuditContext";
import API from "../lib/api";
import { toast } from "sonner";
import { cn } from "../lib/utils";

/* -------------------------------------------------------------------------- */
/*                               UI CONSTANTS                                 */
/* -------------------------------------------------------------------------- */

const severityConfig = {
  high: {
    label: "High",
    badge:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
    dot: "bg-red-500",
  },

  medium: {
    label: "Medium",
    badge:
      "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
    dot: "bg-amber-500",
  },

  low: {
    label: "Low",
    badge:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
};

/* -------------------------------------------------------------------------- */
/*                                HELPERS                                     */
/* -------------------------------------------------------------------------- */

function getSeverity(control) {
  return (
    severityConfig[control.severity?.toLowerCase()] ||
    severityConfig.low
  );
}

/* -------------------------------------------------------------------------- */
/*                             SUMMARY CARD                                   */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  icon: Icon,
  iconClassName,
}) {
  return (
    <Card className="rounded-2xl border border-border/80 shadow-sm bg-card transition-all hover:border-emerald-500/30">
      <CardContent className="flex items-center gap-3.5 p-4 sm:p-5">
        <div
          className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted/70",
            iconClassName
          )}
        >
          <Icon className="h-5 w-5" />
        </div>

        <div>
          <div className="text-2xl font-black tracking-tight leading-none text-foreground">
            {value}
          </div>

          <div className="mt-1 text-xs font-medium text-muted-foreground">
            {label}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*                            QUALITY ISSUES                                  */
/* -------------------------------------------------------------------------- */

function QualityIssues({ issues }) {
  if (!issues.length) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/70 p-3.5 dark:border-amber-900 dark:bg-amber-950/20">
      <div className="space-y-2">
        {issues.map((issue, index) => (
          <div
            key={`${issue.gate}-${index}`}
            className="flex gap-2 text-xs leading-5 text-amber-900 dark:text-amber-200"
          >
            <AlertTriangle className="mt-1 h-3.5 w-3.5 shrink-0" />

            <div>
              <span className="font-mono font-semibold">
                {issue.gate}
              </span>

              <span className="mx-1">—</span>

              {issue.issue}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              CONTROL CARD                                  */
/* -------------------------------------------------------------------------- */

function ControlCard({
  control,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}) {
  const severity = getSeverity(control);

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border/80 bg-card transition-all hover:border-emerald-500/30 shadow-sm",
        expanded && "border-emerald-500/40 ring-1 ring-emerald-500/20 shadow-md"
      )}
    >
      {/* Control summary row */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/40"
      >
        <div className="flex shrink-0 items-center gap-2 mt-0.5">
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}

          <Badge
            variant="secondary"
            className="shrink-0 font-mono text-[10px] px-2.5 py-0.5 rounded-lg bg-muted text-foreground border border-border/60"
          >
            {control.control_id}
          </Badge>
        </div>

        <p className="min-w-0 flex-1 text-xs sm:text-sm font-semibold text-foreground text-left break-words whitespace-normal leading-relaxed">
          {control.description}
        </p>

        <div className="flex shrink-0 items-center gap-2.5 mt-0.5">
          {/* {control.sql_testable && (
            <span
              title="SQL testable"
              className="hidden items-center gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400 md:flex bg-emerald-500/10 px-2.5 py-0.5 rounded-full border border-emerald-500/20"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Automated
            </span>
          )} */}

          <Badge
            variant="outline"
            className={cn(
              "gap-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-lg",
              severity.badge
            )}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                severity.dot
              )}
            />

            {severity.label}
          </Badge>
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t bg-muted/[0.12] p-4">
          {/* Metadata */}
          <div className="grid gap-4 lg:grid-cols-[1fr_1fr_1fr]">
            <MetadataItem
              label="Clause"
              value={control.clause_reference}
            />

            <MetadataItem
              label="Risk"
              value={control.risk}
            />

            <MetadataItem
              label="Frequency"
              value={control.frequency}
            />

            <MetadataItem
              label="Source"
              value={control.source}
            />
          </div>

          <div className="my-4 border-t" />

          {/* Procedure + evidence */}
          <div className="grid gap-5 xl:grid-cols-[1.4fr_0.8fr]">
            <div>
              <SectionTitle>Test Procedure</SectionTitle>

              {control.test_procedure?.length ? (
                <div className="space-y-2">
                  {control.test_procedure.map((step, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2.5 text-sm"
                    >
                      <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-[10px] font-semibold text-emerald-700">
                        {index + 1}
                      </div>

                      <span className="leading-5">
                        {step}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No test procedure provided.
                </p>
              )}
            </div>

            <div>
              <SectionTitle>
                Evidence Required
              </SectionTitle>

              {control.evidence_required?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {control.evidence_required.map(
                    (evidence, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="gap-1.5 py-1 text-xs font-normal"
                      >
                        <FileText className="h-3 w-3" />
                        {evidence}
                      </Badge>
                    )
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No evidence specified.
                </p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex items-center justify-end gap-2 border-t pt-3">
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={() => onEdit(control)}
            >
              <Pencil className="h-3.5 w-3.5" />
              Edit
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 border-red-200 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onDelete(control.id)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function MetadataItem({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>

      <p className="mt-0.5 break-words text-xs font-medium leading-5">
        {value || "—"}
      </p>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/*                             SOURCE SECTION                                 */
/* -------------------------------------------------------------------------- */

function SourceSection({
  source,
  controls,
  expandedControlId,
  setExpandedControlId,
  onEdit,
  onDelete,
}) {
  const [open, setOpen] = useState(false);

  // const issueCount = useMemo(
  //   () =>
  //     controls.reduce(
  //       (total, control) =>
  //         total + getFailedGates(control).length,
  //       0
  //     ),
  //   [controls]
  // );

  return (
    <div className="border-t first:border-t-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-muted/40"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}

        <FileText className="h-3.5 w-3.5 text-muted-foreground" />

        <span
          className="min-w-0 flex-1 truncate text-xs font-medium"
          title={source}
        >
          {source || "Unknown source"}
        </span>

        {/* {issueCount > 0 && (
          <span className="flex items-center gap-1 text-[10px] text-amber-600">
            <AlertTriangle className="h-3 w-3" />
            {issueCount}
          </span>
        )} */}

        <Badge
          variant="secondary"
          className="text-[10px]"
        >
          {controls.length}
        </Badge>
      </button>

      {open && (
        <div className="space-y-2 bg-muted/10 px-3 pb-3 pt-1">
          {controls.map((control) => (
            <ControlCard
              key={control.id}
              control={control}
              expanded={
                expandedControlId === control.id
              }
              onToggle={() =>
                setExpandedControlId((current) =>
                  current === control.id
                    ? null
                    : control.id
                )
              }
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                AREA GROUP                                  */
/* -------------------------------------------------------------------------- */

function AreaGroup({
  area,
  sources,
  defaultOpen,
  expandedControlId,
  setExpandedControlId,
  onEdit,
  onDelete,
}) {
  const [open, setOpen] = useState(defaultOpen);

  const allControls = useMemo(
    () => Object.values(sources).flat(),
    [sources]
  );

  // const issueCount = useMemo(
  //   () =>
  //     allControls.filter(
  //       (control) => getFailedGates(control).length > 0
  //     ).length,
  //   [allControls]
  // );

  const highRiskCount = useMemo(
    () =>
      allControls.filter(
        (control) =>
          control.severity?.toLowerCase() === "high"
      ).length,
    [allControls]
  );

  return (
    <Card className="rounded-3xl border border-border/80 shadow-sm overflow-hidden bg-card">
      <CardHeader className="p-0">
        <button
          type="button"
          onClick={() => setOpen((current) => !current)}
          className={cn(
            "flex w-full items-center gap-3.5 px-6 py-4 text-left transition-colors hover:bg-muted/40",
            open && "bg-muted/20"
          )}
        >
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}

          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
            <Shield className="h-4 w-4" />
          </div>

          <CardTitle className="min-w-0 flex-1 truncate text-sm font-bold text-foreground">
            {area}
          </CardTitle>

          <div className="flex items-center gap-2.5">
            {highRiskCount > 0 && (
              <Badge
                variant="outline"
                className="hidden border-red-500/30 bg-red-500/10 text-[10px] font-bold text-red-600 dark:text-red-400 sm:flex px-2.5 py-0.5 rounded-lg"
              >
                {highRiskCount} high risk
              </Badge>
            )}

            <Badge
              variant="secondary"
              className="text-[10px] font-bold px-2.5 py-0.5 rounded-lg"
            >
              {allControls.length}{" "}
              {allControls.length === 1
                ? "control"
                : "controls"}
            </Badge>
          </div>
        </button>
      </CardHeader>

      {open && (
        <CardContent className="p-0 border-t border-border/60">
          {Object.entries(sources).map(
            ([source, sourceControls]) => (
              <SourceSection
                key={source}
                source={source}
                controls={sourceControls}
                expandedControlId={expandedControlId}
                setExpandedControlId={
                  setExpandedControlId
                }
                onEdit={onEdit}
                onDelete={onDelete}
              />
            )
          )}
        </CardContent>
      )}
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/*                              FILTER BAR                                    */
/* -------------------------------------------------------------------------- */

function FilterBar({
  search,
  setSearch,
  severity,
  setSeverity,
  filteredCount,
  totalCount,
  onRefresh,
}) {
  return (
    <div className="flex flex-wrap items-center gap-3 bg-muted/20 p-3 sm:p-4 rounded-2xl border border-border/80">
      <div className="relative min-w-[240px] flex-1">
        <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />

        <Input
          value={search}
          onChange={(event) =>
            setSearch(event.target.value)
          }
          placeholder="Search control ID, description, source clause..."
          className="h-10 pl-9 pr-9 text-xs rounded-xl bg-card border-border/80 focus-visible:ring-emerald-500/30"
        />

        {search && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => setSearch("")}
            className="absolute right-3.5 top-1/2 -translate-y-1/2"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      <select
        value={severity}
        onChange={(event) =>
          setSeverity(event.target.value)
        }
        className="h-10 rounded-xl border border-border/80 bg-card px-3 text-xs font-semibold text-foreground"
      >
        <option value="all">All risk levels</option>
        <option value="high">High risk</option>
        <option value="medium">Medium risk</option>
        <option value="low">Low risk</option>
      </select>

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-10 gap-1.5 text-xs font-semibold rounded-xl px-4"
        onClick={onRefresh}
      >
        <RefreshCw className="h-3.5 w-3.5 mr-1" />
        Refresh
      </Button>

      <div className="ml-auto text-xs text-muted-foreground font-medium">
        Showing <span className="font-bold text-foreground">{filteredCount}</span> of {totalCount} controls
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                              MAIN PAGE                                     */
/* -------------------------------------------------------------------------- */

export default function Phase3Controls() {
  const navigate = useNavigate();
  const {
    auditRun,
    controls = [],
    fetchControls,
    advancePhase,
  } = useAudit();

  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [editControl, setEditControl] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const [
    expandedControlId,
    setExpandedControlId,
  ] = useState(null);

  useEffect(() => {
    if (auditRun?.id) {
      fetchControls();
    }
  }, [auditRun?.id, fetchControls]);

  const handleExtractControls = async () => {
    if (!auditRun?.id) {
      toast.error("Select an active audit run first");
      return;
    }

    setExtracting(true);
    try {
      const { data } = await API.post(`/audit-runs/${auditRun.id}/controls/extract`);

      if (data?.job_id) {
        const jobId = data.job_id;
        const pollIntervalMs = 2000;
        const maxAttempts = 120; // ~4 minutes for a long batched extraction
        let attempts = 0;

        const poll = async () => {
          try {
            const { data: status } = await API.get(`/job-status/${jobId}`);
            if (status.ready && status.state === "SUCCESS") {
              await fetchControls();
              toast.success("Controls extracted successfully");
              setExtracting(false);
              return;
            }
            if (status.ready && (status.state === "FAILURE" || status.state === "REVOKED")) {
              toast.error(status.error || "Control extraction failed");
              setExtracting(false);
              return;
            }
            attempts += 1;
            if (attempts >= maxAttempts) {
              toast.error("Control extraction timed out. Please try again.");
              setExtracting(false);
              return;
            }
            setTimeout(poll, pollIntervalMs);
          } catch (e) {
            console.error("Failed to poll extraction job:", e);
            toast.error("Failed to check extraction status");
            setExtracting(false);
          }
        };

        setTimeout(poll, pollIntervalMs);
      } else {
        await fetchControls();
        toast.success("Control extracted successfully");
        setExtracting(false);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to extract controls"
      );
      setExtracting(false);
    }
  };

  const stats = useMemo(() => {
    const result = {
      total: controls.length,
      high: 0,
      medium: 0,
      low: 0,
    };

    controls.forEach((control) => {
      const level =
        control.severity?.toLowerCase();

      if (level === "high") result.high += 1;
      else if (level === "medium")
        result.medium += 1;
      else result.low += 1;
    });

    return result;
  }, [controls]);

  const filteredControls = useMemo(() => {
    const query = search.trim().toLowerCase();

    return controls.filter((control) => {
      const matchesSearch =
        !query ||
        [
          control.control_id,
          control.description,
          control.mapped_process_area,
          control.source,
          control.clause_reference,
        ].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query)
        );

      const matchesSeverity =
        severity === "all" ||
        control.severity?.toLowerCase() ===
          severity;

      return matchesSearch && matchesSeverity;
    });
  }, [
    controls,
    search,
    severity,
  ]);

  const groupedControls = useMemo(() => {
    return filteredControls.reduce(
      (groups, control) => {
        const area =
          control.mapped_process_area ||
          "Unassigned";

        const source =
          control.source ||
          "Unknown source";

        if (!groups[area]) {
          groups[area] = {};
        }

        if (!groups[area][source]) {
          groups[area][source] = [];
        }

        groups[area][source].push(control);

        return groups;
      },
      {}
    );
  }, [filteredControls]);

  const handleEdit = (control) => {
    setEditControl({
      id: control.id,
      control_id: control.control_id || "",
      source: control.source || "",
      clause_reference: control.clause_reference || "",
      description: control.description || "",
      mapped_process_area: control.mapped_process_area || "",
      severity: String(control.severity || "LOW").toUpperCase(),
    });
  };

  const handleSaveEdit = async () => {
    if (!auditRun?.id || !editControl?.id) return;

    setEditSaving(true);

    try {
      await API.put(
        `/audit-runs/${auditRun.id}/controls/${editControl.id}`,
        {
          control_id: editControl.control_id,
          source: editControl.source,
          clause_reference: editControl.clause_reference,
          description: editControl.description,
          mapped_process_area: editControl.mapped_process_area,
          severity: editControl.severity,
        }
      );

      toast.success("Control updated");
      setEditControl(null);
      await fetchControls();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Failed to update control"
      );
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (controlId) => {
    if (!auditRun?.id) return;

    const confirmed = window.confirm(
      "Delete this control? This action cannot be undone."
    );

    if (!confirmed) return;

    try {
      await API.delete(
        `/audit-runs/${auditRun.id}/controls/${controlId}`
      );

      if (expandedControlId === controlId) {
        setExpandedControlId(null);
      }

      toast.success("Control deleted");

      await fetchControls();
    } catch (error) {
      toast.error(
        error.response?.data?.detail ||
          "Failed to delete control"
      );
    }
  };

  const clearFilters = () => {
    setSearch("");
    setSeverity("all");
  };

  if (!auditRun) {
    return (
      <PageLayout
        title="Controls"
        subtitle="Select an audit run to review controls"
        icon={ListTree}
      >
        <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-muted">
            <Shield className="h-6 w-6 text-muted-foreground" />
          </div>

          <p className="text-sm font-bold">
            No audit selected
          </p>

          <p className="mt-1 max-w-sm text-xs text-muted-foreground">
            Select an audit run to review its extracted controls.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Phase 3: Control Extraction"
      subtitle={`Review extracted control requirements for ${auditRun.name || auditRun.id}`}
      icon={ListTree}
      className="space-y-6 max-w-7xl mx-auto pb-12"
      data-testid="phase-3"
      actions={
        <div className="flex items-center gap-3">
          {auditRun?.name && (
            <Badge variant="outline" className="hidden sm:inline-flex bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-xs font-semibold px-3 py-1">
              {auditRun.name}
            </Badge>
          )}
          <Button
            onClick={handleExtractControls}
            disabled={extracting}
            data-testid="extract-controls-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-4 shadow-md shadow-emerald-600/30 border-0 transition-all active:scale-95 flex items-center gap-1.5"
          >
            {extracting ? (
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ListTree className="h-3.5 w-3.5" />
            )}
            <span>{extracting ? "Extracting..." : "Extract Controls"}</span>
          </Button>

          <Button
            onClick={async () => {
              await advancePhase(4);
              navigate("/phase/4");
            }}
            data-testid="phase-3-continue-btn"
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-9 px-5 shadow-md shadow-emerald-600/30 border-0 transition-all active:scale-95"
          >
            Continue to Scope <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      }
    >
      {/* COMPACT SUMMARY METRICS */}
      {controls.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">Total Controls</span>
            <span className="text-2xl sm:text-3xl font-black text-foreground mt-1 block">{stats.total}</span>
          </Card>

          <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
            <span className="text-[10px] font-bold uppercase tracking-wider text-red-600 dark:text-red-400 block">High Risk</span>
            <span className="text-2xl sm:text-3xl font-black text-red-600 dark:text-red-400 mt-1 block">{stats.high}</span>
          </Card>

          <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600 dark:text-amber-400 block">Medium Risk</span>
            <span className="text-2xl sm:text-3xl font-black text-amber-600 dark:text-amber-400 mt-1 block">{stats.medium}</span>
          </Card>

          <Card className="rounded-2xl border border-border/80 bg-card p-4 hover:border-emerald-500/30 transition-all">
            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 block">Low Risk</span>
            <span className="text-2xl sm:text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 block">{stats.low}</span>
          </Card>
        </div>
      )}

      {/* FILTER SEARCH BAR */}
      {controls.length > 0 && (
        <FilterBar
          search={search}
          setSearch={setSearch}
          severity={severity}
          setSeverity={setSeverity}
          filteredCount={filteredControls.length}
          totalCount={controls.length}
          onRefresh={fetchControls}
        />
      )}

      {/* EMPTY STATES */}
      {controls.length === 0 && (
        <Card className="rounded-3xl border-border/80 shadow-sm py-16 text-center">
          <CardContent className="space-y-4">
            <div className="h-12 w-12 mx-auto rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
              <ListTree className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">No controls found</h3>
              <p className="text-xs text-muted-foreground max-w-sm mx-auto mt-1">
                Extract controls from your ingested documents to populate this phase.
              </p>
            </div>
            <Button
              onClick={handleExtractControls}
              disabled={extracting}
              data-testid="empty-extract-controls-btn"
              className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-6 shadow-md shadow-emerald-600/30 border-0 transition-all active:scale-95 inline-flex items-center gap-2"
            >
              {extracting ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <ListTree className="h-3.5 w-3.5" />
              )}
              <span>{extracting ? "Extracting Controls..." : "Extract Controls"}</span>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* CONTROL GROUPS */}
      {filteredControls.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <div>
              <h2 className="text-base font-extrabold tracking-tight text-foreground">
                Control Process Areas
              </h2>
              <p className="text-xs text-muted-foreground">
                Extracted controls grouped by process area domain and source file.
              </p>
            </div>

            <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground bg-muted/40 px-3 py-1.5 rounded-xl border border-border/60">
              <SlidersHorizontal className="h-3.5 w-3.5" />
              {Object.keys(groupedControls).length} process area{Object.keys(groupedControls).length === 1 ? "" : "s"}
            </div>
          </div>

          <div className="space-y-4">
            {Object.entries(groupedControls).map(
              ([area, sources], index) => (
                <AreaGroup
                  key={area}
                  area={area}
                  sources={sources}
                  defaultOpen={index === 0}
                  expandedControlId={expandedControlId}
                  setExpandedControlId={setExpandedControlId}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              )
            )}
          </div>
        </div>
      )}

        <Dialog
          open={!!editControl}
          onOpenChange={(open) => {
            if (!open) setEditControl(null);
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Control</DialogTitle>
              <DialogDescription>
                Update the control fields and save them through the control update endpoint.
              </DialogDescription>
            </DialogHeader>

            {editControl && (
              <div className="grid gap-4 px-6 pb-2 pt-1">
                <div className="grid gap-2">
                  <Label className="text-xs font-medium">Control ID</Label>
                  <Input
                    value={editControl.control_id}
                    onChange={(event) =>
                      setEditControl((current) => ({
                        ...current,
                        control_id: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-xs font-medium">Source</Label>
                  <Input
                    value={editControl.source}
                    onChange={(event) =>
                      setEditControl((current) => ({
                        ...current,
                        source: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-xs font-medium">Clause Reference</Label>
                  <Input
                    value={editControl.clause_reference}
                    onChange={(event) =>
                      setEditControl((current) => ({
                        ...current,
                        clause_reference: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-xs font-medium">Mapped Process Area</Label>
                  <Input
                    value={editControl.mapped_process_area}
                    onChange={(event) =>
                      setEditControl((current) => ({
                        ...current,
                        mapped_process_area: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-xs font-medium">Risk</Label>
                  <select
                    value={editControl.severity}
                    onChange={(event) =>
                      setEditControl((current) => ({
                        ...current,
                        severity: event.target.value,
                      }))
                    }
                    className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="LOW">LOW</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="HIGH">HIGH</option>
                  </select>
                </div>

                <div className="grid gap-2">
                  <Label className="text-xs font-medium">Description</Label>
                  <Textarea
                    value={editControl.description}
                    onChange={(event) =>
                      setEditControl((current) => ({
                        ...current,
                        description: event.target.value,
                      }))
                    }
                    rows={4}
                  />
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0 pt-2">
              <Button
                type="button"
                variant="outline"
                className="rounded-xl text-xs font-semibold h-10 px-4"
                onClick={() => setEditControl(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs h-10 px-5 shadow-md shadow-emerald-600/30 border-0 active:scale-95 transition-all"
                onClick={handleSaveEdit}
                disabled={editSaving}
              >
                {editSaving ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </PageLayout>
  );
}
